import { addDays, fromISODate, toISODate } from '../lib/dates';

export type QuickAddResult = { title: string; dueDate: string | null; priority: number };

const WEEKDAYS: Record<string, number> = {
  lunedi: 1,
  martedi: 2,
  mercoledi: 3,
  giovedi: 4,
  venerdi: 5,
  sabato: 6,
  domenica: 0,
};

/** Lowercase without accents: "Venerdì" → "venerdi". */
const normalize = (word: string) => word.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');

/** Next date with that weekday, never today ("lunedì" said on a Monday means next Monday). */
function nextWeekday(reference: string, weekday: number): string {
  const current = fromISODate(reference).getDay();
  const delta = (weekday - current + 7) % 7 || 7;
  return addDays(reference, delta);
}

/** "3/11" or "3/11/2027": the next such date if the year is omitted. */
function explicitDate(word: string, reference: string): string | null {
  const match = word.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?$/);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = match[3] ? Number(match[3]) : undefined;
  const ref = fromISODate(reference);
  let date = new Date(year ?? ref.getFullYear(), month - 1, day);
  if (date.getMonth() !== month - 1) return null;
  if (!year && date < ref) date = new Date(ref.getFullYear() + 1, month - 1, day);
  return toISODate(date);
}

/**
 * Reads a one-line task: "Pagare affitto venerdì !!" → title "Pagare affitto", due next Friday,
 * priority 2. Words: oggi, domani, dopodomani, weekdays, dd/mm[/yyyy]; "!", "!!", "!!!".
 */
export function parseQuickAdd(input: string, reference: string): QuickAddResult {
  let dueDate: string | null = null;
  let priority = 0;
  const kept: string[] = [];

  for (const word of input.trim().split(/\s+/)) {
    const key = normalize(word);
    if (/^!{1,3}$/.test(word)) priority = word.length;
    else if (key === 'oggi') dueDate = reference;
    else if (key === 'domani') dueDate = addDays(reference, 1);
    else if (key === 'dopodomani') dueDate = addDays(reference, 2);
    else if (key in WEEKDAYS) dueDate = nextWeekday(reference, WEEKDAYS[key] ?? 0);
    else {
      const date = explicitDate(word, reference);
      if (date) dueDate = date;
      else kept.push(word);
    }
  }

  const title = kept.join(' ');
  // A line made only of keywords is a title, not a command.
  return title ? { title, dueDate, priority } : { title: input.trim(), dueDate: null, priority: 0 };
}
